/**
 * User Settings V2 — "Personal" section: profile fields (avatar, name,
 * username) plus the display-preference set (time zone, language,
 * measurement system, date/time/number formats) that drives rendering
 * across the rest of the portal.
 *
 * Contains:
 * - PersonalSection — the rendered SettingsSection (default export)
 */

import PersonIcon from '@mui/icons-material/Person';
import { Stack } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  SettingsFieldGroup,
  SettingsFieldRow,
  SettingsSection,
  SettingsSelectField,
  SettingsSubsection,
  SettingsTextField,
  SettingsTimeZoneField,
  UserAvatarField,
} from '@sophocles/views/settings/components';

// Locale list is intentionally short — these are the only locales the
// portal actually ships translations for (en-US, es-ES); the backend
// `UserOption.LOCALE_CHOICES` is a long historical list we don't need
// to expose to end users.
const LOCALE_OPTIONS = [
  { value: 'en-US', code: 'general.english', fallback: 'English' },
  { value: 'es-ES', code: 'general.spanish', fallback: 'Español' },
];

// Static unambiguous sample (April 15, 2026 — day and month differ
// so MM/dd vs dd/MM render distinctly in the dropdown preview).
const dateFormatOptions = [
  { value: 'MM/dd/yyyy', label: '04/15/2026' },
  { value: 'dd/MM/yyyy', label: '15/04/2026' },
  { value: 'yyyy-MM-dd', label: '2026-04-15' },
  { value: 'MMM dd, yyyy', label: 'Apr 15, 2026' },
];

const timeFormatOptions = [
  { value: 'hh:mm a', label: '01:00 PM' },
  { value: 'HH:mm', label: '13:00' },
];

const measurementOptions = [
  { value: 'imperial', code: 'general.imperial', fallback: 'Imperial' },
  { value: 'metric', code: 'general.metric', fallback: 'Metric' },
];

const startOfWeekOptions = [
  { value: 'sunday', code: 'general.sunday', fallback: 'Sunday' },
  { value: 'monday', code: 'general.monday', fallback: 'Monday' },
];

const numberFormatOptions = [
  { value: 'comma-period', label: '1,000.00' },
  { value: 'period-comma', label: '1.000,00' },
  { value: 'no-separator-period', label: '1000.00' },
  { value: 'no-separator-comma', label: '1000,00' },
];

export default function PersonalSection({ avatarUserId, profile, isAdminMode, onAvatarChanged }) {
  const { t } = useTranslation();
  return (
    <SettingsSection
      id="settings-personal"
      icon={<PersonIcon />}
      title={t('userSettings.personal', 'Personal')}
      description={t(
        'userSettings.v2.personalDescription',
        'Your name, email, and how dates, times, and numbers render across the portal.',
      )}
    >
      <Stack spacing={3}>
        <SettingsSubsection title={t('userSettings.profile', 'Profile')}>
          <Stack spacing={2}>
            {avatarUserId ? (
              <UserAvatarField userId={avatarUserId} profile={profile} onChanged={onAvatarChanged} />
            ) : null}
            <SettingsFieldGroup columns={{ xs: 1, sm: 2 }}>
              <SettingsTextField name="profile.first_name" label={t('general.firstName', 'First Name')} />
              <SettingsTextField name="profile.last_name" label={t('general.lastName', 'Last Name')} />
            </SettingsFieldGroup>
            <SettingsTextField
              name="profile.email"
              label={t('general.username', 'Username')}
              type="email"
              disabled={!isAdminMode}
              helperText={
                isAdminMode
                  ? t(
                      'userSettings.adminEditEmailHelper',
                      "As an administrator you can change this user's account email; the user will need to use the new address to sign in.",
                    )
                  : t(
                      'userSettings.emailReadOnly',
                      'Your account email cannot be changed here. Contact support to update it.',
                    )
              }
            />
          </Stack>
        </SettingsSubsection>

        <SettingsSubsection title={t('userSettings.displayPreferences', 'Display Preferences')} bordered>
          <SettingsFieldRow
            label={t('general.timeZone', 'Time Zone')}
            description={t(
              'userSettings.timeZoneDescription',
              'Used for scheduled events, alarm timestamps, and report delivery times.',
            )}
            controlWidth={380}
          >
            <SettingsTimeZoneField name="localization.time_zone" hiddenLabel showCurrentTime={false} />
          </SettingsFieldRow>
          <SettingsFieldRow
            label={t('general.language', 'Language')}
            description={t('userSettings.languageDescription', 'Portal language for menus and messages.')}
          >
            <SettingsSelectField
              name="localization.locale"
              hiddenLabel
              options={LOCALE_OPTIONS.map(o => ({ value: o.value, label: t(o.code, o.fallback) }))}
            />
          </SettingsFieldRow>
          <SettingsFieldRow
            label={t('general.measurementSystem', 'Measurement System')}
            description={t(
              'userSettings.measurementSystemDescription',
              'Imperial or metric units for temperatures, distances, and power readings.',
            )}
          >
            <SettingsSelectField
              name="localization.measurement_system"
              hiddenLabel
              options={measurementOptions.map(o => ({ value: o.value, label: t(o.code, o.fallback) }))}
            />
          </SettingsFieldRow>
          <SettingsFieldRow
            label={t('general.dateFormat', 'Date Format')}
            description={t('userSettings.dateFormatDescription', 'How calendar dates render across the portal.')}
          >
            <SettingsSelectField name="localization.date_format" hiddenLabel options={dateFormatOptions} />
          </SettingsFieldRow>
          <SettingsFieldRow
            label={t('general.timeFormat', 'Time Format')}
            description={t('userSettings.timeFormatDescription', '12-hour or 24-hour clock display.')}
          >
            <SettingsSelectField name="localization.time_format" hiddenLabel options={timeFormatOptions} />
          </SettingsFieldRow>
          <SettingsFieldRow
            label={t('general.startOfWeek', 'Start of Week')}
            description={t('userSettings.startOfWeekDescription', 'Which day calendars and weekly reports begin on.')}
          >
            <SettingsSelectField
              name="localization.start_of_week"
              hiddenLabel
              options={startOfWeekOptions.map(o => ({ value: o.value, label: t(o.code, o.fallback) }))}
            />
          </SettingsFieldRow>
          <SettingsFieldRow
            label={t('general.numberFormat', 'Number Format')}
            description={t('userSettings.numberFormatDescription', 'Thousands separator and decimal mark.')}
            divider={false}
          >
            <SettingsSelectField name="localization.number_format" hiddenLabel options={numberFormatOptions} />
          </SettingsFieldRow>
        </SettingsSubsection>
      </Stack>
    </SettingsSection>
  );
}
